import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, toZonedTime } from 'date-fns-tz';
import { 
  Loader2, 
  MessageCircle, 
  Send, 
  User, 
  Bot,
  CheckCircle,
  XCircle,
  RefreshCw,
  History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserToolHistory } from './UserToolHistory';
import { UserSessionContext } from './UserSessionContext';
import { CannedResponses } from './CannedResponses';
import { ConversationTags } from './ConversationTags';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface Conversation {
  id: string;
  fingerprint: string;
  email: string | null;
  full_name: string | null;
  requires_human: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  last_message?: string;
  unread_count?: number;
  tool_access_id: string | null;
  tags: string[];
}

interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'human_agent';
  content: string;
  created_at: string;
}

export const SupportChatPanel = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [filter, setFilter] = useState<'all' | 'needs_human' | 'active'>('needs_human');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [showToolHistory, setShowToolHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchConversations();

    // Set up realtime subscription for new messages
    const channel = supabase
      .channel('support-messages')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_messages' },
        () => {
          // Refresh conversations when new messages arrive
          fetchConversations();
          if (selectedConversation) {
            fetchMessages(selectedConversation.id);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_conversations' },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoadingConversations(true);
    
    // Fetch conversations
    const { data: convData, error: convError } = await supabase
      .from('support_conversations')
      .select('*')
      .order('updated_at', { ascending: false });

    if (convError) {
      console.error('Error fetching conversations:', convError);
      setLoadingConversations(false);
      return;
    }

    // Fetch last message for each conversation
    const conversationsWithMessages = await Promise.all(
      (convData || []).map(async (conv) => {
        const { data: msgData } = await supabase
          .from('support_messages')
          .select('content, role')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: false })
          .limit(1);

        return {
          ...conv,
          last_message: msgData?.[0]?.content || 'No messages',
        };
      })
    );

    setConversations(conversationsWithMessages);
    setLoadingConversations(false);
  };

  const fetchMessages = async (conversationId: string) => {
    setLoadingMessages(true);
    
    const { data, error } = await supabase
      .from('support_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      setMessages((data || []).map(msg => ({
        ...msg,
        role: msg.role as 'user' | 'assistant' | 'human_agent'
      })));
    }
    setLoadingMessages(false);
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation || sendingReply) return;

    setSendingReply(true);
    const message = replyMessage.trim();
    setReplyMessage('');

    // Optimistically add message
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: selectedConversation.id,
      role: 'human_agent',
      content: message,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMessage]);

    // Insert message via edge function or directly
    const { error: msgError } = await supabase
      .from('support_messages')
      .insert({
        conversation_id: selectedConversation.id,
        role: 'human_agent',
        content: message,
      });

    if (msgError) {
      console.error('Error sending reply:', msgError);
      toast({
        title: 'Failed to send reply',
        description: 'Please try again.',
        variant: 'destructive',
      });
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setReplyMessage(message);
    } else {
      // Update conversation to mark as responded
      await supabase
        .from('support_conversations')
        .update({ 
          requires_human: false,
          updated_at: new Date().toISOString() 
        })
        .eq('id', selectedConversation.id);

      // Refresh messages and conversations
      fetchMessages(selectedConversation.id);
      fetchConversations();
      
      toast({
        title: 'Reply sent',
        description: 'Your response has been delivered.',
      });
    }

    setSendingReply(false);
  };

  const handleCloseConversation = async (conversationId: string) => {
    const { error } = await supabase
      .from('support_conversations')
      .update({ status: 'closed', requires_human: false })
      .eq('id', conversationId);

    if (error) {
      toast({
        title: 'Error closing conversation',
        variant: 'destructive',
      });
    } else {
      fetchConversations();
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      toast({
        title: 'Conversation closed',
      });
    }
  };

  const filteredConversations = conversations.filter(conv => {
    // First apply status filter
    let passesStatusFilter = true;
    switch (filter) {
      case 'needs_human':
        passesStatusFilter = conv.requires_human && conv.status !== 'closed';
        break;
      case 'active':
        passesStatusFilter = conv.status === 'active';
        break;
    }
    
    // Then apply tag filter
    const passesTagFilter = !tagFilter || (conv.tags && conv.tags.includes(tagFilter));
    
    return passesStatusFilter && passesTagFilter;
  });

  const needsHumanCount = conversations.filter(c => c.requires_human && c.status !== 'closed').length;
  
  // Get unique tags from all conversations for filter dropdown
  const allTags = [...new Set(conversations.flatMap(c => c.tags || []))];

  return (
    <div className="flex h-[600px] bg-card rounded-xl border border-border overflow-hidden">
      {/* Conversations List */}
      <div className="w-1/3 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-foreground">Conversations</h3>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={fetchConversations}
              className="h-8 w-8"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setFilter('needs_human')}
              className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'needs_human'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Needs Reply ({needsHumanCount})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'active'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`flex-1 px-2 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All
            </button>
          </div>
          
          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="mt-2">
              <select
                value={tagFilter || ''}
                onChange={(e) => setTagFilter(e.target.value || null)}
                className="w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          {loadingConversations ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageCircle className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {filter === 'needs_human' ? 'No conversations need attention' : 'No conversations found'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredConversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-medium text-sm text-foreground truncate">
                      {conv.full_name || conv.email || `User ${conv.fingerprint.slice(0, 6)}`}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {conv.requires_human && conv.status !== 'closed' && (
                        <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                      )}
                      {conv.status === 'closed' && (
                        <CheckCircle className="w-3 h-3 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mb-1">
                    {conv.last_message}
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    {format(toZonedTime(new Date(conv.updated_at), 'America/New_York'), 'MMM d, h:mm a')}
                  </p>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-foreground">
                    {selectedConversation.full_name || selectedConversation.email || `User ${selectedConversation.fingerprint.slice(0, 6)}`}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {selectedConversation.email || 'No email provided'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConversation.requires_human && (
                    <span className="px-2 py-1 text-xs rounded-full bg-destructive/10 text-destructive">
                      Awaiting Reply
                    </span>
                  )}
                  {selectedConversation.status !== 'closed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCloseConversation(selectedConversation.id)}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Close
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Conversation Tags */}
              <div className="mt-2">
                <ConversationTags
                  conversationId={selectedConversation.id}
                  currentTags={selectedConversation.tags || []}
                  onTagsUpdated={(newTags) => {
                    setSelectedConversation(prev => prev ? { ...prev, tags: newTags } : null);
                    setConversations(prev => prev.map(c => 
                      c.id === selectedConversation.id ? { ...c, tags: newTags } : c
                    ));
                  }}
                />
              </div>
              
              {/* Real-time Session Context */}
              <div className="mt-2 py-2 px-3 rounded-lg bg-muted/50 border border-border/50">
                <UserSessionContext fingerprint={selectedConversation.fingerprint} />
              </div>
              
              {/* Tool Usage History Collapsible */}
              <Collapsible open={showToolHistory} onOpenChange={setShowToolHistory} className="mt-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2">
                    <span className="flex items-center gap-1.5 text-xs">
                      <History className="w-3.5 h-3.5" />
                      Tool Usage History
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {showToolHistory ? 'Hide' : 'Show'}
                    </span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <UserToolHistory 
                    toolAccessId={selectedConversation.tool_access_id} 
                    fingerprint={selectedConversation.fingerprint}
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          message.role === 'user'
                            ? 'bg-muted text-foreground rounded-bl-sm'
                            : message.role === 'human_agent'
                              ? 'bg-green-100 dark:bg-green-900/30 text-foreground rounded-br-sm'
                              : 'bg-primary/10 text-foreground rounded-br-sm'
                        }`}
                      >
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                          {message.role === 'user' ? (
                            <>
                              <User className="w-3 h-3" />
                              User
                            </>
                          ) : message.role === 'human_agent' ? (
                            <>
                              <User className="w-3 h-3" />
                              You
                            </>
                          ) : (
                            <>
                              <Bot className="w-3 h-3" />
                              Bot
                            </>
                          )}
                          <span className="mx-1">•</span>
                          {format(toZonedTime(new Date(message.created_at), 'America/New_York'), 'h:mm a')}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Reply Input */}
            {selectedConversation.status !== 'closed' && (
              <div className="p-4 border-t border-border space-y-2">
                {/* Canned Responses */}
                <div className="flex items-center gap-2">
                  <CannedResponses 
                    onSelect={(content) => setReplyMessage(prev => prev ? `${prev}\n\n${content}` : content)} 
                  />
                  <span className="text-xs text-muted-foreground">Quick insert a template</span>
                </div>
                
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendReply();
                  }}
                  className="flex gap-2"
                >
                  <Input
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    disabled={sendingReply}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={sendingReply || !replyMessage.trim()}>
                    {sendingReply ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Select a conversation to view</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
